import { css } from 'aphrodite';
import * as React from 'react';
import { useCoachingAdherenceScoreboardQuery } from '../../../queries/AIConversation/useCoachingAdherenceScoreboardQuery';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Field, FieldLabel } from '../../../web/components-v2/Field';
import { IUser } from '../../../extViewmodels';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxValue,
} from '../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../web/components/UserCombobox';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { MIN_CALLS_FOR_SHARE } from '../../constants/coachingAdherence';
import {
	ALL_REPS_BOARD,
	type GroupFilterOption,
	NO_GROUP_FILTERS,
	type RepGroupFilters,
	applyGroupFilters,
	buildScoreboardRange,
	groupFilterOptions,
	sendsRepDetails,
	summarizeScoreboard,
	todayDateString,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { ScoreboardSummaryTiles } from './ScoreboardSummaryTiles';
import { ScoreboardTable } from './ScoreboardTable';

// Radix Select reserves the empty string for "no value", so "any" needs a real one.
const ANY = '__any__';

function GroupFilterSelect({
	id,
	label,
	anyLabel,
	value,
	options,
	onChange,
}: {
	id: string;
	label: string;
	anyLabel: string;
	value: string;
	options: GroupFilterOption[];
	onChange: (value: string) => void;
}) {
	return (
		<Field styles={[styleSheet.controlField]}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Select value={value || ANY} onValueChange={next => onChange(next === ANY ? '' : next)}>
				<SelectTrigger id={id} styles={styleSheet.groupTrigger}>
					<SelectValue>{options.find(option => option.value === value)?.label ?? anyLabel}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={ANY}>{anyLabel}</SelectItem>
					{options.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</Field>
	);
}

function GroupFilterMultiSelect({
	id,
	label,
	anyLabel,
	values,
	options,
	onChange,
}: {
	id: string;
	label: string;
	anyLabel: string;
	values: string[];
	options: GroupFilterOption[];
	onChange: (values: string[]) => void;
}) {
	const chipsRef = React.useRef<HTMLDivElement>(null);
	const labelOf = (value: string) => options.find(option => option.value === value)?.label ?? value;
	return (
		<Field styles={[styleSheet.controlField]}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Combobox
				items={options.map(option => option.value)}
				itemToStringLabel={item => labelOf(item as string)}
				multiple
				value={values}
				onValueChange={next => onChange(next as string[])}
			>
				<ComboboxChips ref={chipsRef} styles={[styleSheet.groupTrigger]}>
					<ComboboxValue>
						{(selected: string[]) => (
							<>
								{selected.map(value => (
									<ComboboxChip key={value} aria-label={labelOf(value)}>
										{labelOf(value)}
										<ComboboxChipRemove />
									</ComboboxChip>
								))}
								<ComboboxChipsInput id={id} placeholder={selected.length > 0 ? '' : anyLabel} />
							</>
						)}
					</ComboboxValue>
				</ComboboxChips>
				<ComboboxContent anchor={chipsRef}>
					<ComboboxEmpty>No matches</ComboboxEmpty>
					<ComboboxList>
						{(value: string) => (
							<ComboboxItem key={value} value={value}>
								{labelOf(value)}
							</ComboboxItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
		</Field>
	);
}

export function AdherenceScoreboard() {
	const [startDate, setStartDate] = React.useState(todayDateString);
	const [endDate, setEndDate] = React.useState(todayDateString);
	const [pickedRep, setPickedRep] = React.useState<IUser | null>(null);
	const [groupFilters, setGroupFilters] = React.useState<RepGroupFilters>(NO_GROUP_FILTERS);

	// A rep and a group filter would contradict each other, so choosing one clears the other.
	const onRepChange = (user: IUser | null) => {
		setPickedRep(user);
		setGroupFilters(NO_GROUP_FILTERS);
	};
	const onGroupFilterChange = (change: Partial<RepGroupFilters>) => {
		setGroupFilters(current => ({ ...current, ...change }));
		setPickedRep(null);
	};

	const range = buildScoreboardRange(startDate, endDate);
	const validRange = 'error' in range ? null : range;
	const rangeError = 'error' in range ? range.error : null;
	const scoreboardQuery = useCoachingAdherenceScoreboardQuery({
		cohort: ALL_REPS_BOARD,
		start: validRange?.start ?? null,
		end: validRange?.end ?? null,
	});

	const selectedRep = pickedRep ? (scoreboardQuery.data?.reps.find(rep => rep.userId === pickedRep.id) ?? null) : null;
	const options = groupFilterOptions(scoreboardQuery.data?.reps ?? []);
	// A new range rebuilds the options, so a value they no longer offer would filter invisibly; it's dropped instead.
	const offered = (value: string, list: GroupFilterOption[]) =>
		list.some(option => option.value === value) ? value : '';
	const offeredAll = (values: string[], list: GroupFilterOption[]) =>
		values.filter(value => list.some(option => option.value === value));
	// The manager comes from a search of active users, not the board's own list, which keeps departed managers and ids
	// the goals sheet never resolved; a manager with no reps here leaves an empty board that says so.
	const showCoachMode = sendsRepDetails(scoreboardQuery.data?.reps ?? []);
	const activeFilters: RepGroupFilters = {
		adherenceGroups: offeredAll(groupFilters.adherenceGroups, options.adherenceGroups),
		managerUserId: groupFilters.managerUserId,
		quotaTeam: offered(groupFilters.quotaTeam, options.quotaTeams),
		teams: offeredAll(groupFilters.teams, options.teams),
	};

	const renderBody = () => {
		if (validRange && scoreboardQuery.isLoading) {
			return <LoadingSpinner type='large' />;
		}
		if (!validRange) {
			return null;
		}
		if (scoreboardQuery.isError || !scoreboardQuery.data) {
			return <div className={css(styleSheet.state)}>Couldn&rsquo;t load the adherence scoreboard.</div>;
		}
		const scoreboard = scoreboardQuery.data;
		if (scoreboard.reps.every(rep => rep.judgedCalls === 0)) {
			return (
				<div className={css(styleSheet.state)}>
					No calls were scored by the adherence judge in this range yet. A call is scored a few minutes after the rep
					logs its outcome.
				</div>
			);
		}
		if (pickedRep && !selectedRep) {
			const name = `${pickedRep.firstName ?? ''} ${pickedRep.lastName ?? ''}`.trim() || 'This rep';
			return <div className={css(styleSheet.state)}>{name} isn&rsquo;t on this scoreboard for this range.</div>;
		}
		const reps = selectedRep ? [selectedRep] : applyGroupFilters(scoreboard.reps, activeFilters);
		if (reps.length === 0) {
			return <div className={css(styleSheet.state)}>No reps on this board match these filters.</div>;
		}
		const summary = summarizeScoreboard(reps);
		return (
			<>
				<ScoreboardSummaryTiles
					summary={summary}
					reps={reps}
					selectedRep={selectedRep}
					followedScore={scoreboard.followedScore}
				/>
				<p className={css(styleSheet.headlineNote)}>
					A call counts as followed at a score of {scoreboard.followedScore} or more. Rep counts include only reps with
					at least {MIN_CALLS_FOR_SHARE} calls past the hook.
					{showCoachMode
						? ' Dials per demo counts prospecting and cold demo dials, as sales reporting does; Dials also counts confirm and reschedule dials. Coach mode is each rep’s setting today.'
						: null}
				</p>
				<ScoreboardTable
					key={selectedRep?.userId ?? 'all'}
					scoreboard={scoreboard}
					reps={reps}
					initialExpandedRepId={selectedRep?.userId ?? null}
					range={validRange}
					showCohort
				/>
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controls)}>
				{options.adherenceGroups.length > 0 ? (
					<GroupFilterMultiSelect
						id='scoreboard-adherence-group'
						label='Adherence group'
						anyLabel='All groups'
						values={activeFilters.adherenceGroups}
						options={options.adherenceGroups}
						onChange={adherenceGroups => onGroupFilterChange({ adherenceGroups })}
					/>
				) : null}
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-rep'>Rep</FieldLabel>
					<UserCombobox
						id={pickedRep?.id}
						key={`rep-${pickedRep?.id ?? ''}`}
						onValueChange={(user: IUser | null) => onRepChange(user)}
					>
						<ComboboxInput
							id='scoreboard-rep'
							placeholder='All reps'
							showClear
							styles={[styleSheet.userSearchTrigger]}
						/>
						<ComboboxContent>
							<UserComboboxStatus />
							<ComboboxList>
								{(user: IUser) => <UserComboboxAvatarItem key={`rep-item-${user.id}`} user={user} />}
							</ComboboxList>
						</ComboboxContent>
					</UserCombobox>
				</Field>
				{options.managers.length > 0 ? (
					<Field styles={[styleSheet.controlField]}>
						<FieldLabel htmlFor='scoreboard-manager'>Manager</FieldLabel>
						<UserCombobox
							id={activeFilters.managerUserId || undefined}
							key={`manager-${activeFilters.managerUserId}`}
							onValueChange={(user: IUser | null) => onGroupFilterChange({ managerUserId: user?.id ?? '' })}
						>
							<ComboboxInput
								id='scoreboard-manager'
								placeholder='All managers'
								showClear
								styles={[styleSheet.userSearchTrigger]}
							/>
							<ComboboxContent>
								<UserComboboxStatus />
								<ComboboxList>
									{(user: IUser) => <UserComboboxAvatarItem key={`manager-item-${user.id}`} user={user} />}
								</ComboboxList>
							</ComboboxContent>
						</UserCombobox>
					</Field>
				) : null}
				{options.quotaTeams.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-quota-team'
						label='Quota team'
						anyLabel='All teams'
						value={activeFilters.quotaTeam}
						options={options.quotaTeams}
						onChange={quotaTeam => onGroupFilterChange({ quotaTeam })}
					/>
				) : null}
				{options.teams.length > 0 ? (
					<GroupFilterMultiSelect
						id='scoreboard-team'
						label='Team'
						anyLabel='Any team'
						values={activeFilters.teams}
						options={options.teams}
						onChange={teams => onGroupFilterChange({ teams })}
					/>
				) : null}
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-start'>Start</FieldLabel>
					<DateInput id='scoreboard-start' value={startDate} onChange={setStartDate} />
				</Field>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-end'>End</FieldLabel>
					<DateInput id='scoreboard-end' value={endDate} onChange={setEndDate} />
				</Field>
			</div>
			{rangeError ? (
				<div className={css(styleSheet.rangeError)} role='alert'>
					{rangeError}
				</div>
			) : null}
			{renderBody()}
		</div>
	);
}
